import { useEffect, useState } from "react";
import format from "@shared/formatters";
import Button from "@shared/v2/Button";
import Modal from "@shared/v2/Modal";
import TextComboInput from "@shared/v2/TextComboInput";
import Typography from "@shared/v2/Typography";
import Loading from "../Loader";
import LegacyRecommendedGoals from "./LegacyRecommendedGoals";
import RecommendedGoals from "./RecommendedGoals";
import LetsGetStarted from "./LetsGetStarted";
import UnitSources from "./UnitSources";
import ProductivityStats from "./ProductivityStats";
import ActivityPerClosed from "./ActivityPerClosed";
import ActivityGoals from "./ActivityGoals";
import CompleteAndPrint from "./CompleteAndPrint";
import CalculatingGoals from "./CalculatingGoals";
import { useWizardContext } from "./WizardContext";
import { useFeatureFlags } from "../../../reducers/layoutReducer/selectors";

const WizardInner = () => {
  const { page, show, onClose, wizardData } = useWizardContext();
  const { loading } = wizardData;

  return (
    <Modal
      className="tw-flex tw-items-center tw-justify-center"
      contentClassName={`${page < 3 ? "tw-max-w-[460px]" : "tw-max-w-[700px]"} tw-w-[100vw] tw-max-h-[85vh] tw-flex tw-flex-col tw-gap-[32px]`}
      show={show}
      onHide={onClose}
    >
      {loading && <Loading />}
      {page === 1 && <LetsGetStarted />}
      {page === 2 && <CalculatingGoals />}
      {page === 3 && <RecommendedGoals />}
      {page === 4 && <UnitSources />}
      {page === 5 && <ProductivityStats />}
      {page === 6 && <ActivityPerClosed />}
      {page === 7 && <ActivityGoals />}
      {page === 8 && <CompleteAndPrint />}
    </Modal>
  );
};

const LegacyWizardInner = () => {
  const { show, onClose, year, wizardData } = useWizardContext();
  const { actions, goals, loading } = wizardData;
  const [currentNetIncomeGoal, setCurrentNetIncomeGoal] = useState(goals.currentNetIncomeGoal || 0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);

  useEffect(() => {
    setCurrentNetIncomeGoal(goals.currentNetIncomeGoal);
  }, [goals.currentNetIncomeGoal]);

  useEffect(() => {
    if (!show) {
      setPage(1);
      setError(null);
    }
  }, [show]);

  useEffect(() => {
    if (page !== 2) return undefined;

    actions?.setLocalGoals((g) => ({ ...g, currentNetIncomeGoal }));
    const timeoutId = setTimeout(() => {
      setPage(3);
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [page]);

  return (
    <Modal
      className="tw-flex tw-items-center tw-justify-center"
      contentClassName={`${page < 3 ? "tw-max-w-[460px]" : "tw-max-w-[620px]"} tw-w-[100vw] tw-max-h-[85vh] tw-flex tw-flex-col tw-gap-[32px]`}
      show={show}
      onHide={onClose}
      closeOnClickOutside={false}
    >
      {loading && <Loading />}
      {page === 1 && (
        <>
          <Modal.Header
            title="Let's Get Started"
            description={
              <div className="tw-flex tw-flex-col tw-gap-[16px]">
                <span>
                  Let&apos;s start with how much pretax income you want to take home for the year. That will
                  help us calculate your other goals so they are aligned with achieving your desired net
                  income.
                </span>
                <Typography variant="body-s" className="!tw-italic !tw-text-gray-50">
                  If you don&apos;t know your exact net income goal yet, go ahead and enter an estimate - you
                  can always change it later.
                </Typography>
              </div>
            }
            closeButton
          />
          <Modal.Body>
            <TextComboInput
              isRequired
              leading="$"
              placeholder="0"
              spacing={0}
              value={currentNetIncomeGoal ? format.number(currentNetIncomeGoal, 0) : ""}
              label={`${year} Net Income`}
              onChange={(e) => setCurrentNetIncomeGoal((e.target.value || "").replace(/[^0-9]/g, ""))}
              error={error}
            />
          </Modal.Body>
          <Modal.Footer className="tw-flex tw-justify-between tw-gap-[8px]">
            <Button schema="tertiary" size="medium" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="medium"
              onClick={() => {
                if (!currentNetIncomeGoal) {
                  setError("Please enter your net income goal");
                } else {
                  setError(null);
                  setPage(2);
                }
              }}
            >
              Calculate Goals
            </Button>
          </Modal.Footer>
        </>
      )}
      {page === 2 && <CalculatingGoals />}
      {page === 3 && (
        <LegacyRecommendedGoals show={show} year={year} onClose={onClose} wizardData={wizardData} />
      )}
    </Modal>
  );
};

const TeamProductionWizardModal = () => {
  const featureFlags = useFeatureFlags();

  if (!featureFlags?.goalWizard2) return <LegacyWizardInner />;

  return <WizardInner />;
};

export default TeamProductionWizardModal;
